import { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import {
  requireMessage,
  emailMessage,
  length10Message,
  phoneMessage,
  confirmPasswordMessage,
  telegramMessage,
} from "../../../utils/error-messages";
import { useDispatch, useSelector } from "react-redux";

import Loading from "src/components/Loading";
import adapter from "src/adapter/header";
import { with_auth } from "src/adapter";
import { useTranslation } from "react-i18next";
import SearchSelect from "src/components/SearchSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { getAreaList, getCountryList } from "src/actions/areaActions";
import { getDistrictList } from "src/actions/districtActions";
import {
  addOperatorCompany,
  addPublicCompany,
  getOperatorCompanyList,
} from "src/actions/companyActions";
import { toast } from "react-toastify";

const RegisterCompany = ({ location, history }) => {
  const { t } = useTranslation();

  const schema = yup.object().shape({
    Name: yup.string().required(t(requireMessage)),
    Voen: yup
      .string()
      .required(t(requireMessage))
      .length(10, t(length10Message)),
    Password: yup.string().required(t(requireMessage)),
    ConfirmPassword: yup
      .string()
      .required(t(requireMessage))
      .oneOf([yup.ref("Password"), null], t(confirmPasswordMessage)),
    AuthorizedPersonName: yup.string().required(t(requireMessage)),
    AuthorizedPersonSurname: yup.string().required(t(requireMessage)),
    Phone: yup
      .string()
      .required(t(requireMessage))
      .matches(/^([0-9]{12})$/, {
        message: t(phoneMessage),
        excludeEmptyString: true,
      }),
    Email: yup
      .string()
      .required(t(requireMessage))
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        {
          message: t(emailMessage),
          excludeEmptyString: true,
        }
      ),
    Address: yup.string().required(t(requireMessage)),
    CountryId: yup
      .object()
      .shape({
        label: yup.string().required(t(requireMessage)),
        value: yup.string().required(t(requireMessage)),
      })
      .typeError(t(requireMessage))
      .required(t(requireMessage)),
    AreaId: yup
      .object()
      .shape({
        label: yup.string().required(t(requireMessage)),
        value: yup.string().required(t(requireMessage)),
      })
      .typeError(t(requireMessage))
      .required(t(requireMessage)),
    DistrictId: yup
      .object()
      .shape({
        label: yup.string().required(t(requireMessage)),
        value: yup.string().required(t(requireMessage)),
      })
      .typeError(t(requireMessage))
      .required(t(requireMessage)),
    FounderTelegramNumber: yup
      .string()
      .required(t(requireMessage))
      .matches(/^([0-9]{12})$/, {
        message: t(telegramMessage),
        excludeEmptyString: true,
      }),
    CompanyId: yup
      .object()
      .shape({
        label: yup.string().required(t(requireMessage)),
        value: yup.string().required(t(requireMessage)),
      })
      .typeError(t(requireMessage))
      .required(t(requireMessage)),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  let area = watch("AreaId");

  const dispatch = useDispatch();
  const { is_authenticated, loading: authLoading } = useSelector(
    (state) => state.auth
  );
  const { role } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.companies);

  const [defaultOperatorCompanyOpts, setDefaultOperatorCompanyOpts] = useState(
    []
  );
  const [defaultAreaOpts, setDefaultAreaOpts] = useState([]);
  const [defaultDistrictOpts, setDefaultDistrictOpts] = useState([]);
  const [defaultCountryOpts, setDefaultCountryOpts] = useState([]);
  const { areas, countries } = useSelector((state) => state.area);
  const { operatorCompanies } = useSelector((state) => state.companies);
  const { districts } = useSelector((state) => state.district);

  const redirect = location.search ? location.search.split("=")[1] : "/";

  useEffect(() => {
    if (is_authenticated) {
      history.push(redirect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, is_authenticated, redirect]);

  useEffect(() => {
    dispatch(getOperatorCompanyList());
    dispatch(getCountryList());
    dispatch(getAreaList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (operatorCompanies) {
      let finalResult = operatorCompanies.OperatorCompany.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      setDefaultOperatorCompanyOpts(finalResult);
    }
  }, [operatorCompanies]);

  useEffect(() => {
    if (areas) {
      let finalResult = areas.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      setDefaultAreaOpts(finalResult);
    }
  }, [areas]);

  useEffect(() => {
    if (districts) {
      let finalResult = districts.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      setDefaultDistrictOpts(finalResult);
    }
  }, [districts]);

  useEffect(() => {
    if (countries) {
      let finalResult = countries.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      setDefaultCountryOpts(finalResult);
    }
  }, [countries]);

  const loadOperatorCompanyOptions = async (term) => {
    if (term.length >= 1) {
      const result = await adapter({
        url: `/company/operatory-company?Name=${term}&PageSize=25`,
        method: "GET",
      });

      let finalResult = result.Data.OperatorCompany.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      return finalResult;
    }
  };

  const loadCountryOptions = async (term) => {
    if (term.length >= 1) {
      const result = await adapter({
        url: `/country?Name=${term}&PageSize=25`,
        method: "GET",
        headers: with_auth(),
      });

      let finalResult = result.Data.Country.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      return finalResult;
    }
  };

  const loadAreaOptions = async (term) => {
    if (term.length >= 1) {
      const result = await adapter({
        url: `/area?Name=${term}&PageSize=25`,
        method: "GET",
        headers: with_auth(),
      });

      let finalResult = result.Data.Area.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      return finalResult;
    }
  };

  const loadDistrictOptions = async (term) => {
    if (term.length >= 1) {
      const result = await adapter({
        url: `/district?Name=${term}&AreaId=${
          area && area.value ? area.value : ""
        }&PageSize=25`,
        method: "GET",
        headers: with_auth(),
      });

      let finalResult = result.Data.District.map((item) => {
        return { label: item.Name, value: item.Id };
      });

      return finalResult;
    }
  };

  const handleOperatorCompanyChange = (val) => {
    if (val !== null) {
      setValue("CompanyId", val);
      clearErrors("CompanyId");
    } else {
      setValue("CompanyId", "");
    }
  };

  const handleCountryChange = (val) => {
    if (val !== null) {
      setValue("CountryId", val);
      clearErrors("CountryId");
    } else {
      setValue("CountryId", "");
    }
  };

  const handleAreaChange = (val) => {
    if (val !== null) {
      setValue("AreaId", val);
      clearErrors("AreaId");
      dispatch(getDistrictList({ AreaId: val.value }));
    } else {
      setValue("AreaId", "");
    }

    setValue("DistrictId", null);
  };

  const handleDistrictChange = (val) => {
    if (val !== null) {
      setValue("DistrictId", val);
      clearErrors("DistrictId");
    } else {
      setValue("DistrictId", "");
    }
  };

  const onSubmit = (data) => {
    let formData = {
      CompanyId: +data.CompanyId.value,
      Name: data.Name,
      Voen: data.Voen,
      Email: data.Email,
      Password: data.Password,
      Phone: data.Phone,
      AuthorizedPersonName: data.AuthorizedPersonName,
      AuthorizedPersonSurname: data.AuthorizedPersonSurname,
      Address: data.Address,
      AreaId: +data.AreaId.value,
      DistrictId: +data.DistrictId.value,
      CountryId: +data.CountryId.value,
      FounderTelegramNumber: data.FounderTelegramNumber,
      Note: data.Note || undefined,
    };

    dispatch(addPublicCompany(formData)).then(() => {
      reset({
        Name: null,
        Voen: null,
        Email: null,
        Password: null,
        ConfirmPassword: null,
        Phone: null,
        AuthorizedPersonName: null,
        AuthorizedPersonSurname: null,
        Address: null,
        AreaId: null,
        DistrictId: null,
        CountryId: null,
        CompanyId: null,
        FounderTelegramNumber: null,
        Note: null,
      });

      toast.success(t("register.addCompanySuccess"), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
      });

      setTimeout(() => {
        history.push("/login");
      }, 3000);
    });
  };

  return (
    <div className="c-app c-default-layout flex-row align-items-center">
      {(authLoading || loading) && <Loading />}
      <Container className="py-5">
        <div className="d-flex align-items-center mb-5">
          <a href="/">
            <img
              style={{ width: "60px" }}
              src="/images/logo.png"
              className="img-fluid"
              alt="logo"
            />
          </a>

          <div className="mx-auto">
            <h2 className="text-center">{t("register.companyRegistration")}</h2>
            <span className="d-block text-center">
              <span class="text-danger">*</span> {t("register.asterisk")}
            </span>
          </div>
        </div>

        <div className="login__form">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="Name">
                    <span className="text-danger">*</span> {t("company.name")}
                  </label>
                  <input
                    type="text"
                    id="Name"
                    {...register("Name")}
                    className="form-control custom-input"
                  />
                  {errors.Name && (
                    <span className="invalid-feedback d-block">
                      {errors.Name.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label>
                    <span className="text-danger">*</span>{" "}
                    {t("company.operatorCompany")}
                  </label>
                  <Controller
                    name="CompanyId"
                    control={control}
                    render={({ field }) => (
                      <SearchSelect
                        defaultOpts={defaultOperatorCompanyOpts}
                        loadOpts={loadOperatorCompanyOptions}
                        field={field}
                        onSelChange={handleOperatorCompanyChange}
                      />
                    )}
                  />

                  {errors.CompanyId && (
                    <span className="invalid-feedback d-block">
                      {errors.CompanyId.value?.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="Voen">
                    <span className="text-danger">*</span> {t("company.voen")}
                  </label>
                  <input
                    type="text"
                    id="Voen"
                    {...register("Voen")}
                    className="form-control custom-input"
                  />
                  {errors.Voen && (
                    <span className="invalid-feedback d-block">
                      {errors.Voen.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="FounderTelegramNumber">
                    <span className="text-danger">*</span>{" "}
                    {t("company.founderTelegramNumber")}
                  </label>
                  <input
                    type="text"
                    id="FounderTelegramNumber"
                    {...register("FounderTelegramNumber")}
                    className="form-control custom-input"
                  />
                  {errors.FounderTelegramNumber ? (
                    <span className="invalid-feedback d-block">
                      {errors.FounderTelegramNumber?.message}
                    </span>
                  ) : (
                    <small className="form-text text-muted">
                      ({t("utils.eg")} 994507776655)
                    </small>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="Password">
                    <span className="text-danger">*</span> {t("login.password")}
                  </label>
                  <input
                    type="password"
                    id="Password"
                    {...register("Password")}
                    className="form-control custom-input"
                  />
                  {errors.Password && (
                    <span className="invalid-feedback d-block">
                      {errors.Password.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="ConfirmPassword">
                    <span className="text-danger">*</span>{" "}
                    {t("login.confirmPassword")}
                  </label>
                  <input
                    type="password"
                    id="ConfirmPassword"
                    {...register("ConfirmPassword")}
                    className="form-control custom-input"
                  />
                  {errors.ConfirmPassword && (
                    <span className="invalid-feedback d-block">
                      {errors.ConfirmPassword.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="AuthorizedPersonName">
                    <span className="text-danger">*</span>{" "}
                    {t("register.authorizedPersonFirstName")}
                  </label>
                  <input
                    type="text"
                    id="AuthorizedPersonName"
                    {...register("AuthorizedPersonName")}
                    className="form-control custom-input"
                  />
                  {errors.AuthorizedPersonName && (
                    <span className="invalid-feedback d-block">
                      {errors.AuthorizedPersonName.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="AuthorizedPersonSurname">
                    <span className="text-danger">*</span>{" "}
                    {t("register.authorizedPersonLastName")}
                  </label>
                  <input
                    type="text"
                    id="AuthorizedPersonSurname"
                    {...register("AuthorizedPersonSurname")}
                    className="form-control custom-input"
                  />
                  {errors.AuthorizedPersonSurname && (
                    <span className="invalid-feedback d-block">
                      {errors.AuthorizedPersonSurname.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="Phone">
                    <span className="text-danger">*</span> {t("user.phone")}
                  </label>

                  <input
                    type="text"
                    id="Phone"
                    {...register("Phone")}
                    className="form-control custom-input"
                  />
                  {errors.Phone ? (
                    <span className="invalid-feedback d-block">
                      {errors.Phone?.message}
                    </span>
                  ) : (
                    <small className="form-text text-muted">
                      ({t("utils.eg")} 994507776655)
                    </small>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="email">
                    <span className="text-danger">*</span> {t("login.email")}
                  </label>
                  <input
                    type="text"
                    id="email"
                    {...register("Email")}
                    className="form-control custom-input"
                  />
                  {errors.Email && (
                    <span className="invalid-feedback d-block">
                      {errors.Email.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="Address">
                    <span className="text-danger">*</span> {t("user.address")}
                  </label>
                  <input
                    type="text"
                    id="Address"
                    {...register("Address")}
                    className="form-control custom-input"
                  />
                  {errors.Address && (
                    <span className="invalid-feedback d-block">
                      {errors.Address.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label>
                    <span className="text-danger">*</span> {t("utils.country")}
                  </label>
                  <Controller
                    name="CountryId"
                    control={control}
                    render={({ field }) => (
                      <SearchSelect
                        defaultOpts={defaultCountryOpts}
                        loadOpts={loadCountryOptions}
                        field={field}
                        onSelChange={handleCountryChange}
                      />
                    )}
                  />

                  {errors.CountryId && (
                    <span className="invalid-feedback d-block">
                      {errors.CountryId.value?.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label>
                    <span className="text-danger">*</span> {t("utils.area")}
                  </label>
                  <Controller
                    name="AreaId"
                    control={control}
                    render={({ field }) => (
                      <SearchSelect
                        defaultOpts={defaultAreaOpts}
                        loadOpts={loadAreaOptions}
                        field={field}
                        onSelChange={handleAreaChange}
                      />
                    )}
                  />

                  {errors.AreaId && (
                    <span className="invalid-feedback d-block">
                      {errors.AreaId.value?.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label>
                    <span className="text-danger">*</span> {t("utils.district")}
                  </label>
                  <Controller
                    name="DistrictId"
                    control={control}
                    render={({ field }) => (
                      <SearchSelect
                        defaultOpts={defaultDistrictOpts}
                        loadOpts={loadDistrictOptions}
                        field={field}
                        onSelChange={handleDistrictChange}
                        isDisabled={!area}
                      />
                    )}
                  />

                  {errors.DistrictId && (
                    <span className="invalid-feedback d-block">
                      {errors.DistrictId.value?.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="Note">{t("utils.note")}</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    style={{ maxWidth: 500, height: "unset", resize: "none" }}
                    placeholder={t("utils.note")}
                    {...register("Note")}
                  />
                </div>
              </Col>

              <div className="d-flex justify-content-between align-items-center">
                <button type="submit" className="btn btn-custom">
                  {t("register.default")}
                </button>
              </div>
            </Row>
          </form>
        </div>
      </Container>
    </div>
  );
};

export default RegisterCompany;
